import React, { CSSProperties } from "react";
import styled from "styled-components";

import CustomCheckbox from "../customCheckbox";
import TableCell from "./tableCell";
import {
    ItemSelectBehavior,
    TableDefinition,
} from "features/shared/types/table";
import traverseObject from "features/shared/functions/traverseObject";
import { useDispatch, useSelector } from "services/redux/rootReducer";
import { EditorAction } from "features/shared/types/actions.editor";
import { ModalName } from "features/shared/types/modal";
import { TableAction } from "features/shared/types/actions.table";

interface Props<T> {
    isSelected: boolean;
    row: T;
    onRowClickHandler: (e: React.MouseEvent) => void;
    onModalPreloadCallback?: (id: string) => Promise<T>; // fires before modal is open, returning initial data override
    style: CSSProperties;
}

const TableRow = <T extends { [key: string]: any }>({
    isSelected,
    row,
    onRowClickHandler,
    onModalPreloadCallback,
    style,
}: Props<T>) => {
    const { rowTemplate } = useSelector((state) => state.table.tableDefinition);

    const dispatch = useDispatch();
    const { data, indexer } = useSelector((state) => state.editor);

    const handleViewOrUpdate = async () => {
        const latestSelectedItemIdx = data.findIndex(
            (item) => item[indexer] === row[indexer]
        );
        const latestSelectedItem = data[latestSelectedItemIdx];

        dispatch({
            type: TableAction.SET_SELECTED_ITEMS,
            selectedItems: [],
        });
        dispatch({
            type: TableAction.ADD_SELECTED_ITEM,
            item: row,
            idx: latestSelectedItemIdx,
            itemSelectBehavior: ItemSelectBehavior.Radio,
        });

        dispatch({
            type: EditorAction.TOGGLE_MODAL,
            modal: {
                name: ModalName.UPDATE,
                params: {
                    originalFields: onModalPreloadCallback
                        ? await onModalPreloadCallback(row[indexer])
                        : latestSelectedItem,
                },
            },
        });
    };

    const displayedCells = rowTemplate
        .filter((rowData) => rowData.isShown)
        .map((rowData) => (
            <TableCell key={rowData.accessor}>
                {(rowData.rowAccessor
                    ? rowData.rowAccessor(row)
                    : traverseObject(row, rowData.accessor)) || "-"}
            </TableCell>
        ));

    return (
        <StyledTableRow
            rowTemplate={rowTemplate}
            isSelected={isSelected}
            onClick={onRowClickHandler}
            onDoubleClick={handleViewOrUpdate}
            style={style}
        >
            <TableCell>
                <CustomCheckbox
                    onClickHandler={onRowClickHandler}
                    isChecked={isSelected}
                />
            </TableCell>
            {displayedCells}
        </StyledTableRow>
    );
};

const StyledTableRow = styled.tr<{
    isSelected: boolean;
    rowTemplate: TableDefinition["rowTemplate"];
}>`
    width: 100%;

    position: absolute;

    display: grid;
    grid-template-columns: ${(props) => {
        const checkboxTemplate = `2.5rem`;
        const generatedTemplate = props.rowTemplate
            .filter((cellTemplate) => cellTemplate.isShown)
            .map(({ flex, contentWidth, minCellWidth }) =>
                contentWidth || minCellWidth
                    ? `${contentWidth ?? minCellWidth ?? 0}px`
                    : `minmax(${(minCellWidth ?? 100) / 16}rem, ${flex || 1}fr)`
            )
            .join(" ");
        return `${checkboxTemplate} ${generatedTemplate}`;
    }};

    &:hover {
        cursor: pointer;
    }
    & > * {
        background: ${({ isSelected, theme }) =>
            isSelected ? theme.colors.warning[100] : "inherit"};
        border-bottom: solid 0.5px ${(props) => props.theme.colors.neutral[200]};
    }
    @media (min-width: 768px) {
        &:hover {
            * {
                background: ${({ isSelected, theme }) =>
                    isSelected
                        ? theme.colors.warning[200]
                        : theme.colors.warning[100]};
                cursor: pointer;
            }
        }
    }
`;

export default TableRow;
